/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * Single simulation card. Renders either:
 *   - <video controls> when simulation.video_url is set
 *   - 16:9 "Video coming soon" placeholder when null
 * Plus a feature-flagged "Run live simulation" button when the row
 * has is_live_enabled=true and a modal_endpoint_url, and the
 * FLAGS.LIVE_SIMULATION flag is on.
 */

import React, { useState } from 'react';
import { Play, Zap } from 'lucide-react';
import { FLAGS } from '../../lib/featureFlags';
import { requestLiveSimulation } from '../../lib/simulationClient';

const formatDuration = (sec) => {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const SimulationPlayer = ({ simulation: sim }) => {
  const [liveStatus, setLiveStatus] = useState(null);
  const duration = formatDuration(sim.duration_seconds);
  const hasVideo = Boolean(sim.video_url);

  const liveEnabled =
    FLAGS.LIVE_SIMULATION && sim.is_live_enabled && sim.modal_endpoint_url;

  const handleLive = async () => {
    setLiveStatus({ kind: 'loading' });
    const res = await requestLiveSimulation(sim.slug, {});
    // Backend not wired yet — surface a friendly message either way.
    setLiveStatus(
      res && res.error
        ? { kind: 'message', text: 'Coming soon — Modal endpoint not connected yet.' }
        : { kind: 'success', text: 'Live simulation requested.' }
    );
  };

  return (
    <div className="flex flex-col bg-white border border-brand-border rounded-brandCard overflow-hidden transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand">

      {/* Video / placeholder — 16:9 */}
      <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
        {hasVideo ? (
          <video
            controls
            preload="metadata"
            poster={sim.thumbnail_url || undefined}
            className="absolute inset-0 w-full h-full bg-black"
          >
            <source src={sim.video_url} />
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-brand-bgOff2 text-center px-4">
            <div className="w-14 h-14 rounded-full bg-white border-2 border-brand-cyan flex items-center justify-center mb-3">
              <Play className="w-5 h-5 text-brand-cyan ml-0.5" aria-hidden="true" />
            </div>
            <p className="text-sm font-semibold text-brand-text">
              Video coming soon
            </p>
            <p className="mt-1 text-xs text-brand-muted2">
              Recording in progress
            </p>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-bold text-base text-brand-text leading-snug">
          {sim.title}
        </h3>

        {sim.description && (
          <p className="mt-2 text-sm text-brand-muted leading-relaxed">
            {sim.description}
          </p>
        )}

        {duration && (
          <p className="mt-3 text-xs text-brand-muted2">
            {hasVideo ? 'Duration' : 'Expected duration'}: {duration}
          </p>
        )}

        {liveEnabled && (
          <div className="mt-4 pt-4 border-t border-brand-border">
            <button
              type="button"
              onClick={handleLive}
              disabled={liveStatus?.kind === 'loading'}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Zap className="w-4 h-4" aria-hidden="true" />
              {liveStatus?.kind === 'loading' ? 'Requesting…' : 'Run live simulation →'}
            </button>
            {liveStatus?.text && (
              <p className="mt-2 text-xs text-brand-muted italic">
                {liveStatus.text}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimulationPlayer;
